import axios from 'axios';
import { getStoredToken, removeStoredToken } from '../utils/storage';
import { ROUTES } from '../config/constants';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const USE_MOCK_API = process.env.REACT_APP_USE_MOCK_API === 'true';

if (!API_BASE_URL && !USE_MOCK_API) {
    console.error('API_BASE_URL is not defined in environment variables');
}

const api = axios.create({
    baseURL: API_BASE_URL || 'http://localhost:3002', // Port 3002 für dein Backend
    headers: {
        'Content-Type': 'application/json',
    },
});

// Request interceptor to add auth token
api.interceptors.request.use(
    (config) => {
        const token = getStoredToken();
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Response interceptor to handle errors
api.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401 && !error.config?.url?.includes('/auth/login')) {
            removeStoredToken();
            window.location.href = ROUTES.LOGIN;
        }
        return Promise.reject(error);
    }
);

// Password initialization function
export const initializePassword = async (passwordData) => {
    try {
        const response = await api.post('/auth/init-password', {
            email: passwordData.email,
            old_password: passwordData.oldPassword,
            new_password: passwordData.newPassword,
        });

        return {
            success: true,
            data: response.data,
        };
    } catch (error) {
        console.error('Password initialization error:', error);

        return {
            success: false,
            error: error.response?.data?.detail || error.message || 'Ein Fehler ist aufgetreten',
            status: error.response?.status,
        };
    }
};

// Validate password strength
export const validatePassword = async (password) => {
    try {
        const response = await api.post('/auth/validate-password', { password });
        return {
            success: true,
            data: response.data,
        };
    } catch (error) {
        return {
            success: false,
            error: error.response?.data?.detail || error.message,
            status: error.response?.status,
        };
    }
};

// Get current user info
export const getCurrentUser = async () => {
    try {
        const response = await api.get('/me');
        return {
            success: true,
            data: response.data,
        };
    } catch (error) {
        return {
            success: false,
            error: error.response?.data?.detail || error.message,
            status: error.response?.status,
        };
    }
};

// Get all users
export const getUsers = async (skip = 0, limit = 100) => {
    try {
        const response = await api.get('/users', {
            params: { skip, limit }
        });
        return {
            success: true,
            data: response.data,
        };
    } catch (error) {
        return {
            success: false,
            error: error.response?.data?.detail || error.message,
            status: error.response?.status,
        };
    }
};

// Create new user
export const createUser = async (userData) => {
    try {
        const response = await api.post('/users', userData);
        return {
            success: true,
            data: response.data,
        };
    } catch (error) {
        return {
            success: false,
            error: error.response?.data?.detail || error.message,
            status: error.response?.status,
        };
    }
};

// Get assignments
export const getAssignments = async (userId = null, mandantId = null) => {
    try {
        const params = {};
        if (userId) params.user_id = userId;
        if (mandantId) params.mandant_id = mandantId;

        const response = await api.get('/assignments', { params });
        return {
            success: true,
            data: response.data,
        };
    } catch (error) {
        return {
            success: false,
            error: error.response?.data?.detail || error.message,
            status: error.response?.status,
        };
    }
};

// Create assignment
export const createAssignment = async (assignmentData) => {
    try {
        const response = await api.post('/assignments', assignmentData);
        return {
            success: true,
            data: response.data,
        };
    } catch (error) {
        return {
            success: false,
            error: error.response?.data?.detail || error.message,
            status: error.response?.status,
        };
    }
};

// Health check
export const healthCheck = async () => {
    try {
        const response = await api.get('/health');
        return {
            success: true,
            data: response.data,
        };
    } catch (error) {
        return {
            success: false,
            error: error.response?.data?.detail || error.message,
            status: error.response?.status,
        };
    }
};

// Login function - ruft dein Backend auf, das sich um IDP kümmert
export const loginUser = async (credentials) => {
    try {
        const response = await api.post('/auth/login', {
            username: credentials.username,
            password: credentials.password,
        });

        return {
            success: true,
            token: response.data.token,
            user: response.data.user,
        };
    } catch (error) {
        console.error('Login error:', error);

        return {
            success: false,
            error: error.response?.data?.detail || error.message || 'Login fehlgeschlagen',
            status: error.response?.status,
        };
    }
};

// Logout function
export const logoutUser = async () => {
    try {
        await api.post('/auth/logout');
        return {
            success: true,
        };
    } catch (error) {
        console.error('Logout error:', error);
        return {
            success: true, // Logout sollte immer erfolgreich sein
        };
    }
};

// Get user mandanten (tenants) from backend
export const getUserMandanten = async () => {
    try {
        const response = await api.get('/me');

        // Extrahiere Mandanten aus der User-Response
        const user = response.data;
        const mandanten = user.mandanten || [];

        // Transformiere Backend-Format zu Frontend-Format
        const transformedMandanten = mandanten.map(mandant => ({
            id: `mandant-${mandant.mandant_id}`,
            name: mandant.institut_name,
            description: mandant.role_in_mandant || 'Benutzer',
            domain: mandant.tenant_url || `mandant-${mandant.mandant_id}`,
            mandant_id: mandant.mandant_id,
            is_primary: mandant.is_primary,
            access_level: mandant.access_level,
            legal: mandant.legal || '',
        }));

        return {
            success: true,
            data: transformedMandanten,
        };
    } catch (error) {
        console.error('Get mandanten error:', error);
        return {
            success: false,
            error: error.response?.data?.detail || error.message,
            status: error.response?.status,
        };
    }
};

// Get rundschreiben update information for all sources
export const getRundschreibenUpdates = async () => {
    try {
        const response = await api.get('/api/rundschreiben/updates');
        return {
            success: true,
            data: response.data,
        };
    } catch (error) {
        console.error('Rundschreiben updates error:', error);
        return {
            success: false,
            error: error.response?.data?.detail || error.message || 'Fehler beim Laden der Rundschreiben-Updates',
            status: error.response?.status,
        };
    }
};

// Get BaFin rundschreiben data
export const getBaFinRundschreiben = async () => {
    try {
        const response = await api.get('/api/rundschreiben/bafin');
        return {
            success: true,
            data: response.data,
        };
    } catch (error) {
        console.error('BaFin rundschreiben error:', error);
        return {
            success: false,
            error: error.response?.data?.detail || error.message || 'BaFin Rundschreiben nicht verfügbar',
            status: error.response?.status,
        };
    }
};

// Get Bundesbank rundschreiben data
export const getBundesbankRundschreiben = async () => {
    try {
        const response = await api.get('/api/rundschreiben/bundesbank');
        return {
            success: true,
            data: response.data,
        };
    } catch (error) {
        console.error('Bundesbank rundschreiben error:', error);
        return {
            success: false,
            error: error.response?.data?.detail || error.message || 'Bundesbank Rundschreiben nicht verfügbar',
            status: error.response?.status,
        };
    }
};

// Search all rundschreiben sources
export const searchRundschreiben = async (query, source = null) => {
    try {
        const params = { query };
        if (source) {
            params.source = source; // 'bafin', 'bundesbank' or null for all
        }

        const response = await api.get('/api/rundschreiben/search', { params });
        return {
            success: true,
            data: response.data,
        };
    } catch (error) {
        console.error('Rundschreiben search error:', error);
        return {
            success: false,
            error: error.response?.data?.detail || error.message || 'Fehler bei der Rundschreiben-Suche',
            status: error.response?.status,
        };
    }
};

// Get specific rundschreiben data with retry mechanism
export const getRundschreibenData = async (source, retries = 2) => {
    let lastError = null;

    for (let attempt = 0; attempt <= retries; attempt++) {
        try {
            let result;

            switch (source.toLowerCase()) {
                case 'bafin':
                    result = await getBaFinRundschreiben();
                    break;
                case 'bundesbank':
                    result = await getBundesbankRundschreiben();
                    break;
                default:
                    throw new Error(`Unbekannte Rundschreiben-Quelle: ${source}`);
            }

            if (result.success) {
                return result;
            }

            lastError = result.error;

            // Bei 5xx Fehlern retry, bei 4xx nicht
            if (result.status && result.status >= 400 && result.status < 500) {
                break; // Kein Retry bei Client-Fehlern
            }

            // Kurze Wartezeit vor dem nächsten Versuch
            if (attempt < retries) {
                await new Promise(resolve => setTimeout(resolve, 1000 * (attempt + 1)));
            }

        } catch (error) {
            lastError = error.message;

            if (attempt < retries) {
                await new Promise(resolve => setTimeout(resolve, 1000 * (attempt + 1)));
            }
        }
    }

    return {
        success: false,
        error: lastError || 'Rundschreiben-Daten konnten nicht geladen werden',
    };
};

// Get rundschreiben health status (for debugging)
export const getRundschreibenHealth = async () => {
    try {
        const updates = await getRundschreibenUpdates();

        if (!updates.success) {
            return {
                success: false,
                error: updates.error,
                overall_status: 'error',
                sources: {}
            };
        }

        const { bafin, bundesbank, overall_status } = updates.data;

        return {
            success: true,
            data: {
                overall_status,
                sources: {
                    bafin: {
                        status: bafin.status,
                        available: bafin.status === 'available',
                        last_modified: bafin.last_modified,
                        error: bafin.error_message
                    },
                    bundesbank: {
                        status: bundesbank.status,
                        available: bundesbank.status === 'available',
                        last_modified: bundesbank.last_modified,
                        error: bundesbank.error_message
                    }
                },
                timestamp: new Date().toISOString()
            }
        };
    } catch (error) {
        console.error('Rundschreiben health check error:', error);
        return {
            success: false,
            error: error.message || 'Health check fehlgeschlagen',
            overall_status: 'unknown',
            sources: {}
        };
    }
};

// Utility function to format rundschreiben dates
export const formatRundschreibenDate = (isoString, options = {}) => {
    if (!isoString) return null;

    try {
        const date = new Date(isoString);

        const defaultOptions = {
            day: '2-digit',
            month: 'long',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            timeZone: 'Europe/Berlin',
            ...options
        };

        return date.toLocaleString('de-DE', defaultOptions);
    } catch (error) {
        console.error('Date formatting error:', error);
        return 'Ungültiges Datum';
    }
};

// Get rundschreiben statistics (if needed later)
export const getRundschreibenStats = async (source = null) => {
    try {
        const params = source ? { source } : {};
        const response = await api.get('/api/rundschreiben/stats', { params });

        return {
            success: true,
            data: response.data,
        };
    } catch (error) {
        console.error('Rundschreiben stats error:', error);
        return {
            success: false,
            error: error.response?.data?.detail || error.message || 'Statistiken nicht verfügbar',
            status: error.response?.status,
        };
    }
};

// Get rundschreiben file structure (secured)
export const getRundschreibenFiles = async (source, ordnerPfad) => {
    try {
        // Korrekte URL-Behandlung
        const normalizedPath = ordnerPfad.replace(/\\/g, '/');
        const encodedPath = encodeURIComponent(normalizedPath).replace(/%2F/g, '/');

        const response = await api.get(`/api/rundschreiben/${source}/files/${encodedPath}`);
        return {
            success: true,
            data: response.data,
        };
    } catch (error) {
        console.error(`Rundschreiben ${source} files error:`, error);
        return {
            success: false,
            error: error.response?.data?.detail || error.message || `${source} Dateistruktur nicht verfügbar`,
            status: error.response?.status,
        };
    }
};

// Download filtered rundschreiben files (secured)
export const downloadRundschreibenFiltered = async (source, ordnerPfad, fileTypes = ['.html', '.pdf']) => {
    try {
        // Korrekte URL-Behandlung
        const normalizedPath = ordnerPfad.replace(/\\/g, '/');
        const encodedPath = encodeURIComponent(normalizedPath).replace(/%2F/g, '/');

        const response = await api.post(
            `/api/rundschreiben/${source}/download-filtered/${encodedPath}`,
            { fileTypes },
            { responseType: 'blob' }
        );

        return {
            success: true,
            data: response.data,
            headers: response.headers,
        };
    } catch (error) {
        console.error(`Rundschreiben ${source} download error:`, error);
        return {
            success: false,
            error: error.response?.data?.detail || error.message || `${source} Download nicht verfügbar`,
            status: error.response?.status,
        };
    }
};

// Get AI analysis (secured)
export const getRundschreibenAIAnalysis = async (source, ordnerPfad, unterordner, filename) => {
    try {
        // Korrekte URL-Behandlung
        const normalizedPath = ordnerPfad.replace(/\\/g, '/');
        const encodedPath = encodeURIComponent(normalizedPath).replace(/%2F/g, '/');

        const response = await api.get(
            `/api/rundschreiben/${source}/ai-analysis/${encodedPath}/${unterordner}/${filename}`
        );

        return {
            success: true,
            data: response.data,
        };
    } catch (error) {
        console.error(`Rundschreiben ${source} AI analysis error:`, error);
        return {
            success: false,
            error: error.response?.data?.detail || error.message || `${source} KI-Auswertung nicht verfügbar`,
            status: error.response?.status,
        };
    }
};

// Get detailed rundschreiben info (secured)
export const getRundschreibenInfo = async (source, ordnerPfad) => {
    try {
        // Korrekte URL-Behandlung
        const normalizedPath = ordnerPfad.replace(/\\/g, '/');
        const encodedPath = encodeURIComponent(normalizedPath).replace(/%2F/g, '/');

        const response = await api.get(`/api/rundschreiben/${source}/info/${encodedPath}`);
        return {
            success: true,
            data: response.data,
        };
    } catch (error) {
        console.error(`Rundschreiben ${source} info error:`, error);
        return {
            success: false,
            error: error.response?.data?.detail || error.message || `${source} Info nicht verfügbar`,
            status: error.response?.status,
        };
    }
};

// Utility function to check available files with retry
export const checkRundschreibenFiles = async (source, ordnerPfad, retries = 1) => {
    let lastError = null;

    for (let attempt = 0; attempt <= retries; attempt++) {
        try {
            const result = await getRundschreibenFiles(source, ordnerPfad);

            if (result.success) {
                // Filtere nur HTML und PDF Dateien für Download
                const downloadableFiles = result.data.files?.filter(file =>
                    file.toLowerCase().endsWith('.html') ||
                    file.toLowerCase().endsWith('.pdf')
                ) || [];

                // Prüfe ob KI-Auswertung verfügbar ist
                const auswertungFolder = source === 'bafin' ? 'auswertung_bafin_gesamt' : 'auswertung_buba_gesamt';
                const hasAIAnalysis = result.data.folders?.includes(auswertungFolder) || false;

                return {
                    success: true,
                    hasDownloadableFiles: downloadableFiles.length > 0,
                    hasAIAnalysis: hasAIAnalysis,
                    downloadableFiles: downloadableFiles,
                    totalFiles: result.data.total_files,
                    totalFolders: result.data.total_folders
                };
            }

            lastError = result.error;

            // Bei 4xx Fehlern kein Retry
            if (result.status && result.status >= 400 && result.status < 500) {
                break;
            }

            // Kurze Wartezeit vor dem nächsten Versuch
            if (attempt < retries) {
                await new Promise(resolve => setTimeout(resolve, 1000 * (attempt + 1)));
            }

        } catch (error) {
            lastError = error.message;

            if (attempt < retries) {
                await new Promise(resolve => setTimeout(resolve, 1000 * (attempt + 1)));
            }
        }
    }

    return {
        success: false,
        hasDownloadableFiles: false,
        hasAIAnalysis: false,
        downloadableFiles: [],
        error: lastError || 'Dateiprüfung fehlgeschlagen'
    };
};

// Download handler with proper file naming
export const handleSecureDownload = async (source, ordnerPfad, nummer) => {
    try {
        const result = await downloadRundschreibenFiltered(source, ordnerPfad);

        if (result.success) {
            const blob = result.data;
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${nummer || 'rundschreiben'}_dokumente.zip`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);

            return { success: true };
        } else {
            return { success: false, error: result.error };
        }
    } catch (error) {
        console.error('Secure download error:', error);
        return { success: false, error: 'Download-Fehler aufgetreten' };
    }
};

export default api;